import React, { useEffect, useState } from 'react';
import SearchableSelect from '../SearchableSelect';
import MultiSelect from '../segments/MultiSelect';

const geoSummary = (geos = []) => {
  if (!geos.length) return 'no GEO';
  if (geos.length <= 2) return geos.join(', ');
  return `${geos.slice(0, 2).join(', ')} +${geos.length - 2}`;
};

const SegmentStep = ({ formData, updateFormData, segments, setIsFormValid, channel = 'sms' }) => {
  const isEmail = channel === 'email';
  const [emailCount, setEmailCount] = useState(null);

  const emailIds = (formData.segmentIds || []).map(String);
  const selectionKey = JSON.stringify(emailIds);
  const selectedSegments = segments.filter((s) => emailIds.includes(String(s.value)));
  const selected = isEmail
    ? selectedSegments.length ? {geos: [...new Set(selectedSegments.flatMap((s) => s.geos || []))], segment_type: `${selectedSegments.length} selected`} : null
    : segments.find((s) => String(s.value) === String(formData.segmentId));

  // Для email показываем фактическое число писем: игроки с email, без повторов
  // адреса и без отписавшихся — по тем же правилам, что и снимок кампании.
  useEffect(() => {
    if (!isEmail || !emailIds.length) {
      setEmailCount(null);
      return;
    }
    const controller = new AbortController();
    setEmailCount({ loading: true, selectionKey });
    fetch('/api/v1/audience/count', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content },
      body: JSON.stringify({ channel: 'email', segment_ids: emailIds }),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('count failed'))))
      .then((data) => {
        if (controller.signal.aborted) return;
        setEmailCount({...data, selectionKey});
        updateFormData({ emailRecipients: data.recipients });
      })
      .catch((error) => { if (error.name !== 'AbortError') setEmailCount({ error: true, selectionKey }); });
    return () => controller.abort();
  }, [isEmail, selectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ok = isEmail
      ? emailIds.length > 0 && emailCount?.selectionKey === selectionKey && emailCount.recipients > 0
      : !!formData.segmentId;
    setIsFormValid(ok);
  }, [formData.segmentId, selectionKey, isEmail, emailCount, setIsFormValid]);

  const handleEmailChange = (ids) => {
    setEmailCount(null);
    updateFormData({segmentIds: ids, segmentId: ids[0] || null, emailRecipients: null});
  };

  const handleChange = (id) => {
    const seg = segments.find((s) => String(s.value) === String(id));
    updateFormData({
      segmentId: id || null,
      segmentGeo: seg ? seg.geo : null,
      segmentGeos: seg ? seg.geos || [] : [],
      segmentUncoveredGeos: seg ? seg.uncovered_geos || [] : [],
      senderIdId: null,
    });
  };

  return (
    <div className='space-y-4'>
      <div>
        <label className='block text-gray-700 text-sm font-bold mb-2' htmlFor={isEmail ? 'campaign-email-segments' : 'segment'}>
          {isEmail ? 'Segments *' : 'Segment *'}
        </label>
        {isEmail ? <>
          <MultiSelect inputId='campaign-email-segments' options={segments.map((s) => ({value: String(s.value), label: s.label}))}
            value={emailIds} onChange={handleEmailChange} placeholder='Select segments…' />
          {emailIds.length > 0 && <button type='button' className='text-sm underline mt-1'
            onClick={() => handleEmailChange([])}>Clear selection</button>}
        </> : <SearchableSelect
          options={segments.map((s) => ({
            value: s.value,
            label: isEmail
              ? `${s.label} · ${geoSummary(s.geos)} · ${s.email_players_count ?? '?'} with email · ${s.segment_type}`
              : `${s.label} · ${geoSummary(s.geos)} · ${s.players_count} players · ${s.segment_type}`,
          }))}
          value={formData.segmentId || ''}
          onChange={handleChange}
          placeholder='Select a segment…'
          isClearable
        />}
        <p className='mt-1 text-xs text-gray-500'>
          {isEmail
            ? 'Players from any selected segment are included. Each player and email address receives one email; unsubscribed and blocked addresses are excluded.'
            : "The campaign audience comes from the selected segment. A segment may hold players from several countries — each message is then routed by the player's own GEO."}
        </p>
      </div>

      {selected && (
        <div className='bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-2'>
          <div className='flex items-center gap-2 flex-wrap'>
            {(selected.geos || []).length ? (
              selected.geos.map((geo) => (
                <span key={geo} className='px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs'>
                  {geo}
                </span>
              ))
            ) : (
              <span className='px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-xs'>no GEO yet</span>
            )}
            {!isEmail && <span className='text-slate-600 ml-1'>{selected.players_count} players</span>}
            <span className='text-slate-400 text-xs'>{selected.segment_type}</span>
          </div>

          {isEmail && (
            <div className='text-sm'>
              {emailCount?.loading && <span className='text-slate-500'>Counting email recipients…</span>}
              {emailCount?.error && <span className='text-red-600'>Could not count recipients — the player database is unavailable.</span>}
              {emailCount && emailCount.recipients !== undefined && (
                <>
                  <span className='font-semibold text-slate-900'>{emailCount.recipients} email recipients</span>
                  <span className='text-xs text-slate-500 ml-2'>
                    {emailCount.with_email} with a valid email
                    {emailCount.suppressed > 0 ? ` · ${emailCount.suppressed} unsubscribed / bounced / complained excluded` : ''}
                  </span>
                  {emailCount.recipients === 0 && (
                    <p className='text-xs text-amber-700 mt-1'>Nobody in the selected segments can receive the email.</p>
                  )}
                </>
              )}
            </div>
          )}

          {!isEmail && (selected.uncovered_geos || []).length > 0 && (
            <p className='text-xs text-amber-700'>
              No Sender ID binding for {selected.uncovered_geos.join(', ')} — messages to
              those players will fail. Add a binding on the Sender IDs page.
            </p>
          )}
        </div>
      )}

      {segments.length === 0 && (
        <p className='text-sm text-amber-600'>
          No segments yet. Create one in the Segments section first.
        </p>
      )}
    </div>
  );
};

export default SegmentStep;
